"use client";

import { useEffect, useRef, useState } from "react";
import { IoChevronBackOutline, IoChevronForwardOutline } from "react-icons/io5";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import CommonImage from "@/components/common/image/CommonImage";
import CourseCard from "@/components/common/card/CourseCard";
import HomePageService from "@/services/api/HomeService";
import { Empty, Spin } from "antd";
import { useSelector } from "react-redux";
import SkeletonCourseSection from "@/components/common/skeletons/SkeletonCourseSection";
import { getProgramWiseCourses } from "@/app/actions/courseActions";

export default function CourseSection({
  initialPrograms = [],
  initialCourses = [],
}) {
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const setting = useSelector((state) => state.setting.data);
  const swiperRef = useRef(null);

  useEffect(() => {
    const allCoursesProgram = {
      name: "সকল কোর্স",
      icon_url: "/images/home-page/all-courses.avif",
      slug: "all",
    };
    const updatedPrograms = [allCoursesProgram, ...initialPrograms];
    setPrograms(updatedPrograms);
    setSelectedProgram(allCoursesProgram);
    // console.log("initialCourses", initialCourses);

    const initialCoursesData = initialCourses?.courses?.data || [];
    setCourses(initialCoursesData);
    setCurrentPage(initialCourses?.courses?.current_page || 1);
    setLastPage(initialCourses?.courses?.last_page || 1);
  }, []);

  const handleProgramChange = async (program) => {
    if (selectedProgram?.slug === program.slug) return;

    setSelectedProgram(program);
    setLoading(true);

    try {
      const params = { page: 1 };
      if (program.slug !== "all") {
        params.slug = program.slug;
      }

      console.log("Fetching courses for program:", program.slug, params);

      const result = await getProgramWiseCourses(params);
      console.log("API Response:", result);

      if (result.success) {
        const newCourses = result.data?.courses?.data || [];
        setCourses(newCourses);
        setCurrentPage(result.data?.courses?.current_page || 1);
        setLastPage(result.data?.courses?.last_page || 1);
      } else {
        console.error("API Error:", result.error);
      }
    } catch (err) {
      console.error("Failed to fetch courses:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (loadingMore || currentPage >= lastPage) return;

    setLoadingMore(true);
    try {
      const params = { page: currentPage + 1 };
      if (selectedProgram.slug !== "all") {
        params.slug = selectedProgram.slug;
      }

      const result = await getProgramWiseCourses(params);

      if (result.success) {
        const newCourses = result.data?.courses?.data || [];
        setCourses((prev) => [...prev, ...newCourses]);
        setCurrentPage(result.data?.courses?.current_page || 1);
        setLastPage(result.data?.courses?.last_page || 1);
      }
    } catch (err) {
      console.error("Failed to load more courses:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const nextBtn = document.querySelector(".swiper-button-next");

    const handleNextClick = () => {
      const swiper = swiperRef.current;
      if (swiper?.isEnd) {
        handleLoadMore();
      }
    };

    nextBtn?.addEventListener("click", handleNextClick);

    return () => {
      nextBtn?.removeEventListener("click", handleNextClick);
    };
  }, [currentPage, lastPage, loadingMore]);
  return (
    <>
      {/* {loading ? (
        <SkeletonCourseSection />
      ) : ( */}
      <section className="course_section section_padding">
        <div className="container">
          <div className="course-section-card">
            {/* Section Title */}
            {setting && (
              <div className="section_title">
                <h2 className="title">
                  <span className="w-8 h-8">
                    <CommonImage
                      src="/images/home-page/book-icon.avif"
                      className="img-icon"
                      alt="Book Icon"
                    />
                  </span>{" "}
                  {selectedProgram?.name ?? selectedProgram?.course_title}
                </h2>
                <p className="short-des">{setting?.course_sub_title}</p>
              </div>
            )}

            {/* Program Filters */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-6 my-5 md:my-8 course_prorams">
              {programs.map((item, index) => (
                <div
                  key={index}
                  className={`program-item cursor-pointer`}
                  onClick={() => handleProgramChange(item)}
                >
                  <div
                    className={`item-card ${
                      selectedProgram?.slug === item.slug ? "active" : ""
                    }`}
                  >
                    <div className="program-icon">
                      <CommonImage
                        src={item.icon_url}
                        className="img-icon"
                        alt="Program Icon"
                      />
                    </div>
                    <div className="item-content">
                      <h4 className="title">{item.name}</h4>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Course Slider */}
            <div className="course-slider">
              <div className="courses">
                {courses.length > 0 ? (
                  <Swiper
                    onSwiper={(swiper) => (swiperRef.current = swiper)}
                    modules={[Navigation, Autoplay]}
                    slidesPerView={2}
                    spaceBetween={12}
                    loop={false}
                    speed={1000}
                    navigation={{
                      nextEl: ".swiper-button-next",
                      prevEl: ".swiper-button-prev",
                    }}
                    autoplay={{
                      delay: 3000,
                      disableOnInteraction: false,
                    }}
                    breakpoints={{
                      768: { slidesPerView: 2, spaceBetween: 20 },
                      992: { slidesPerView: 3, spaceBetween: 20 },
                      1200: { slidesPerView: 4, spaceBetween: 20 },
                    }}
                  >
                    {courses.map((course, index) => (
                      <SwiperSlide key={index}>
                        <CourseCard course={course} />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                ) : (
                  <Empty description="No course available" />
                )}
              </div>

              {/* Arrows */}
              {courses.length > 0 && (
                <div className="course_arrow">
                  <div className="swiper-button-prev">
                    <button className="arrow-icon">
                      <IoChevronBackOutline />
                    </button>
                  </div>
                  <div className="swiper-button-next">
                    <button className="arrow-icon">
                      <IoChevronForwardOutline />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* )} */}
    </>
  );
}
